import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Search } from "lucide-react";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";
import SEO, { breadcrumbSchema } from "@/components/seo/SEO";

const CATEGORIES = [
    { id: "all", label: "Todas" },
    { id: "turismo", label: "Turismo" },
    { id: "cultura", label: "Cultura" },
    { id: "eventos", label: "Eventos" },
    { id: "urbanismo", label: "Ciudad" },
];

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

function guessCategory(item) {
    const text = `${item.title || ""} ${item.summary || ""}`.toLowerCase();
    if (/festival|concierto|cultura|patrimonio|castillo|museo|exposición/.test(text)) return "cultura";
    if (/evento|feria|jornada|congreso|encuentro/.test(text)) return "eventos";
    if (/obra|reforma|ayuntamiento|paseo|calle|mejora|urban/.test(text)) return "urbanismo";
    if (/turismo|hotel|alojamiento|playa|kitesurf|viajero|visitante|estrecho/.test(text)) return "turismo";
    return "turismo";
}

export default function News() {
    const { data: items, loading } = useFreshData(
        async () => {
            const r = await api.get("/tarifa-news", { params: { limit: 60 } });
            return Array.isArray(r.data) ? r.data : [];
        },
        { initialData: [] }
    );
    const { data: ownNews } = useFreshData(
        async () => {
            const r = await api.get("/news");
            return Array.isArray(r.data) ? r.data : [];
        },
        { initialData: [] }
    );
    const featuredOwn = useMemo(
        () => (ownNews || []).filter((n) => n.featured && n.published),
        [ownNews]
    );
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const list = items || [];
        return list.filter((n) => {
            const cat = guessCategory(n);
            if (category !== "all" && cat !== category) return false;
            if (!s) return true;
            return (
                (n.title || "").toLowerCase().includes(s) ||
                (n.summary || "").toLowerCase().includes(s)
            );
        });
    }, [items, category, q]);

    return (
        <div data-testid="news-page" className="bg-white">
            <SEO
                title="Noticias sobre Tarifa · Actualidad turística y cultural"
                description="Noticias sobre Tarifa actualizadas a diario: turismo, cultura, eventos y mejoras urbanas. Selección de las noticias más positivas y relevantes para visitantes y sector turístico."
                path="/noticias"
                jsonLd={breadcrumbSchema([
                    { name: "Inicio", path: "/" },
                    { name: "Noticias", path: "/noticias" },
                ])}
            />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-6 sm:pt-24">
                <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                    Noticias · Tarifa
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#082050] tracking-tight">
                    Noticias al día sobre Tarifa.
                </h1>
                <p className="mt-4 text-lg text-[#4A5568] max-w-2xl">
                    Selección diaria de noticias sobre turismo, cultura, eventos y
                    mejoras urbanas de Tarifa. Nos quedamos con lo positivo y lo
                    relevante para visitantes y para el sector turístico.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between border-t border-b border-[#E2E4E9] py-4">
                    <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setCategory(c.id)}
                                data-testid={`news-filter-${c.id}`}
                                className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
                                    category === c.id
                                        ? "bg-[#082050] text-white border-[#082050]"
                                        : "border-[#E2E4E9] text-[#4A5568] hover:border-[#082050]"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <div className="relative w-full md:w-72">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
                        <input
                            data-testid="news-search"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar noticia..."
                            aria-label="Buscar noticia"
                            className="pl-9 border border-[#E2E4E9] px-4 py-2 text-sm w-full focus:outline-none focus:border-[#082050]"
                        />
                    </div>
                </div>
            </section>

            {/* Noticias propias destacadas (Hito ASATTA) */}
            {featuredOwn.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
                    {featuredOwn.map((n) => (
                        <Link
                            key={n.slug}
                            to={`/noticias/${n.slug}`}
                            data-testid={`news-featured-${n.slug}`}
                            className="group grid grid-cols-1 md:grid-cols-5 border-2 border-[#C9A227] bg-white hover:shadow-[0_8px_30px_rgba(201,162,39,0.15)] transition-shadow"
                        >
                            {n.cover_image && (
                                <div className="md:col-span-2 aspect-[16/10] md:aspect-auto md:h-full overflow-hidden bg-[#F8F9FA]">
                                    <img
                                        src={n.cover_image}
                                        alt={`Imagen de la noticia: ${n.title}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                            )}
                            <div className="md:col-span-3 p-7 sm:p-10 flex flex-col">
                                <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
                                    <span className="inline-flex items-center gap-1.5 bg-[#C9A227]/15 text-[#8A6D1A] border border-[#C9A227]/40 px-2.5 py-1 font-semibold">
                                        <Award size={12} />
                                        Hito ASATTA
                                    </span>
                                    {n.category && (
                                        <span className="text-[#082050]/70 font-medium border border-[#082050]/20 px-2.5 py-1">
                                            {n.category}
                                        </span>
                                    )}
                                    <span className="text-[#718096] px-1 py-1">
                                        {formatDate(n.date || n.created_at)}
                                    </span>
                                </div>
                                <h3 className="mt-4 text-2xl sm:text-3xl font-semibold text-[#082050] tracking-tight leading-tight">
                                    {n.title}
                                </h3>
                                <p className="mt-3 text-sm sm:text-base text-[#4A5568] leading-relaxed">
                                    {n.excerpt}
                                </p>
                                <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#082050] group-hover:gap-3 transition-all">
                                    Leer más
                                    <ArrowRight size={14} />
                                </span>
                            </div>
                        </Link>
                    ))}
                </section>
            )}

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24">
                {loading && (
                    <div data-testid="news-loading" className="py-20 text-center text-[#4A5568]">
                        Cargando noticias...
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div data-testid="news-empty" className="py-20 text-center text-[#4A5568]">
                        No hemos encontrado noticias con esos criterios.
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((n, i) => {
                        const cat = guessCategory(n);
                        const catLabel = CATEGORIES.find((c) => c.id === cat)?.label || "Tarifa";
                        return (
                            <a
                                key={n.id || `${i}-${n.link}`}
                                href={n.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`news-item-${i}`}
                                className="border border-[#E2E4E9] hover:border-[#082050] transition-colors group flex flex-col bg-white"
                            >
                                {n.image && (
                                    <div className="aspect-[16/10] overflow-hidden bg-[#F8F9FA]">
                                        <img
                                            src={n.image}
                                            alt={`Imagen de la noticia: ${n.title}`}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                )}
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
                                        <span className="text-[#082050]/70 font-medium">
                                            {catLabel}
                                        </span>
                                        <span className="text-[#718096]">
                                            {formatDate(n.published_at || n.fetched_at)}
                                        </span>
                                    </div>
                                    <h3 className="mt-3 text-lg font-semibold text-[#082050] tracking-tight leading-snug">
                                        {n.title}
                                    </h3>
                                    {n.summary && (
                                        <p className="mt-3 text-sm text-[#4A5568] leading-relaxed line-clamp-4">
                                            {n.summary}
                                        </p>
                                    )}
                                    {n.source && (
                                        <div className="mt-4 text-xs text-[#718096]">
                                            Fuente: {n.source}
                                        </div>
                                    )}
                                    <div className="mt-auto pt-6 inline-flex items-center gap-2 text-xs font-medium text-[#082050]">
                                        Leer artículo original
                                        <ArrowRight size={12} />
                                    </div>
                                </div>
                            </a>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
